// src/hooks/useSheets.js
// Google Apps Script 웹앱과 통신

const SHEETS_URL = process.env.REACT_APP_SHEETS_URL;

async function sheetsRequest(action, extraData = {}) {
  if (!SHEETS_URL || SHEETS_URL === "YOUR_GOOGLE_APPS_SCRIPT_URL_HERE") {
    throw new Error("SHEETS_URL_NOT_SET");
  }

  const response = await fetch(SHEETS_URL, {
    method: "POST",
    headers: { "Content-Type": "text/plain" }, // Apps Script CORS 이슈로 text/plain 사용
    body: JSON.stringify({ action, ...extraData }),
    redirect: "follow",
  });

  const text = await response.text();
  return JSON.parse(text);
}

// Apps Script GET 공용 — 응답이 중간에 끊기면 JSON.parse가 던진다.
// 모바일 테더링 등 불안정한 회선에서 큰 응답(포트폴리오 ~12KB)이 자주 유실돼
// 단발 호출은 실패율이 높다. GET은 멱등이라 재시도해도 안전.
// (쓰기(sheetsRequest)는 응답만 유실돼도 서버엔 반영됐을 수 있어 재시도하지 않는다.)
async function getJSON(action, tries = 3) {
  let lastErr;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      const response = await fetch(`${SHEETS_URL}?action=${action}`, { redirect: "follow", cache: "no-store" });
      return JSON.parse(await response.text());
    } catch (e) { lastErr = e; }
    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
  }
  throw lastErr || new Error(`${action} failed`);
}

export async function fetchPortfolio() {
  return getJSON("getPortfolio");
}

// Momo 포트폴리오 지표(Beta/STM/MTM) — 로컬 push_momo.py가 갱신한 최신 스냅샷
export async function fetchMomo() {
  return getJSON("getMomo");
}

// 종목별 Momo(베타/STM/MTM) — {code: {beta,stm,mtm}}. 네트워크 드롭(VPN) 대비 재시도.
export async function fetchMomoStocks() {
  const url = `${SHEETS_URL}?action=getMomoStocks`;
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const response = await fetch(url, { redirect: "follow", cache: "no-store" });
      const text = await response.text();
      const j = JSON.parse(text);
      if (j && j.stocks && Object.keys(j.stocks).length) return j;
      lastErr = new Error("empty");
    } catch (e) { lastErr = e; }
    await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
  }
  throw lastErr || new Error("fetchMomoStocks failed");
}

// 일별 성과 — push_momo가 당일 포트 수익률 누적. [{date, portRet}] (코스피 대비 BM용)
export async function fetchPerfDaily() {
  return getJSON("getPerfDaily");
}

// 특징주 — kr_daily가 매일 push (시총상위 Gainers·외인/기관 순매수 상위). {asof, stocks:[{c,n,pct?,frgn?,inst?,tag}]}
export async function fetchFeatureStocks() {
  return getJSON("getFeatureStocks");
}

// 섹터 논거/이벤트 — 일 1회 생성된 thesis. {sector: {summary, drivers, catalysts, ...}}
export async function fetchSectorTheses() {
  return getJSON("getSectorTheses");
}

// KR 레짐 게이지 — 포트폴리오와 동일 시트 백엔드(SHEETS_URL)에서 읽음 (별도 토큰 불필요)
export async function fetchRegime() {
  return getJSON("getRegime");
}

export async function savePortfolio(holdings) {
  return sheetsRequest("savePortfolio", { data: holdings });
}

export async function addHolding(holding) {
  return sheetsRequest("addHolding", { holding });
}

export async function updateHolding(holding) {
  return sheetsRequest("updateHolding", { holding });
}

export async function deleteHolding(ticker) {
  return sheetsRequest("deleteHolding", { ticker });
}

export async function fetchTrades() {
  return getJSON("getTrades");
}

export async function addTrade(trade) {
  return sheetsRequest("addTrade", { trade });
}

export async function updateTrade(trade) {
  return sheetsRequest("updateTrade", { trade });
}

export async function deleteTrade(rowIndex) {
  return sheetsRequest("deleteTrade", { rowIndex });
}

// 복기 아이디어(롱숏 페어) — 기기 간 공유 위해 시트 저장. 목록을 통째로 덮어쓴다.
export async function fetchIdeas() {
  return getJSON("getIdeas");
}

export async function saveIdeas(ideas) {
  return sheetsRequest("saveIdeas", { data: ideas });
}
